import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { CreateCommunityForm, CreateBusinessForm, CreateUserForm } from "../../../../types/forms";
import authService from "../../../../services/authService";
import { StepFlowStep } from "../../../components/misc/StepFlow";
import NameInput from "../../../components/forms/profile/NameInput";
import Button from "../../../components/buttons/Button";
import TermsAndConditions from "../../../components/misc/TermsAndConditions";

type LoginRegisterViewProps = {
  setStep: (step: 1 | -1) => void;
  community?: CreateCommunityForm;
  business?: CreateBusinessForm;
}

export default function LoginRegisterView({ community, business, setStep }: LoginRegisterViewProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isInBusinessMode = process.env.REACT_APP_APPLICATION_VERSION === 'BUSINESS';

  const [view, setView] = useState<'LOGIN' | 'REGISTER'>('REGISTER');
  const [formData, setFormData] = useState<CreateUserForm>({});
  const [terms, setTerms] = useState<boolean>(false);

  const handleRegister = async (): Promise<void> => {
    if (!formData.firstName || !formData.lastName || !formData.email) {
      toast.error(t('toast.error.allFields'));
      return;
    }
    if (!terms) {
      toast.error(t('toast.error.terms'));
      return;
    }
    
    if (isInBusinessMode && business) {
      await authService.instantRegisterBusiness(formData, business);
    } else if (!isInBusinessMode && community) {
      await authService.instantRegisterCommunity(formData, community);
    }
    navigate('/overview');
  };

  const handleLogin = async (): Promise<void> => {
    if (!formData.email) {
      toast.error(t('toast.error.allFields'));
      return;
    }
    await authService.generateMagicLink(formData.email);
    toast.success(t('toast.success.sentMagicLink'));
  };

  return (
    <StepFlowStep
      showNext={false}
      showPrev
      setStep={setStep}
      onSubmit={view === 'REGISTER' ? handleRegister : handleLogin}
      disabled={!formData.email}
      submitText={view === 'REGISTER' ? t('page.onboarding.confirmation.submit') : t('auth.sendLink')}
      title={t(`page.onboarding.confirmation.${view.toLowerCase()}.title`)}
      subtitle={t(`page.onboarding.confirmation.${view.toLowerCase()}.subtitle`)}>
      <>
        <div className="flex w-full flex-col gap-4">
          {view === 'REGISTER' && (
            <NameInput
              alias={`${formData.firstName || ''}+${formData.lastName || ''}`}
              setAlias={(alias: string) => {
                const [firstName, lastName] = alias.split('+');
                setFormData({ ...formData, firstName, lastName });
              }}
            />
          )}
          <div className="w-full">
            <p className="mb-1 pr-2 font-medium">Email</p>
            <input
              type="text"
              placeholder="Email"
              className="w-full"
              value={formData.email || ''}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
          </div>
          <div className="flex gap-1 text-sm text-primary-900">
            {view === 'REGISTER' ? (
              <>
                {t('auth.alreadyHaveAccount')}
                <Button className="px-0 py-0 underline" onClick={(): void => setView('LOGIN')}>
                  {t('auth.login')}
                </Button>
              </>
            ) : (
              <>
                {t('auth.noAccountYet')}
                <Button className="px-0 py-0 underline" onClick={(): void => setView('REGISTER')}>
                  {t('auth.register')}
                </Button>
              </>
            )}
          </div>
          {view === 'REGISTER' && <TermsAndConditions state={terms} setState={setTerms} />}
        </div>
      </>
    </StepFlowStep>
  );
}